<script lang="ts" setup>
import { computed, nextTick, onBeforeUnmount, ref, watch } from "vue";
import {
  onBeforeRouteLeave,
  useRoute,
  useRouter,
  type RouteLocationNormalized,
} from "vue-router";
import { useClientHandle } from "@urql/vue";
import { onClickOutside } from "@vueuse/core";
import { useI18n } from "vue-i18n";
import { Breadcrumbs } from "@/components";
import PresentationAnalyticsDocumentRow from "./PresentationAnalyticsDocumentRow.vue";
import PresentationAnalyticsWorkspaceFilter from "./PresentationAnalyticsWorkspaceFilter.vue";
import {
  ensurePresentationAnalyticsSelectorEntryId,
  presentationAnalyticsSelectorEntryState,
  savePresentationAnalyticsSelectorSnapshot,
  takePresentationAnalyticsSelectorSnapshot,
} from "../presentationAnalyticsNavigation";
import {
  getPresentationAnalyticsSelectorMode,
  readSelectorQueryValue,
  selectorQueryForMode,
  type PresentationAnalyticsSelectorResult,
} from "./presentationAnalyticsSelector";

const route = useRoute();
const router = useRouter();
const clientHandle = useClientHandle();
const { t } = useI18n();
const selectorEntryId = ensurePresentationAnalyticsSelectorEntryId();

let restoredSnapshot =
  takePresentationAnalyticsSelectorSnapshot<PresentationAnalyticsSelectorResult>(
    selectorEntryId,
    route.fullPath,
  );
const searchInput = ref("");
const results = ref<PresentationAnalyticsSelectorResult[]>(
  restoredSnapshot?.results ?? [],
);
const hasMore = ref(restoredSnapshot?.hasMore ?? false);
const loading = ref(restoredSnapshot == null);
const loadingMore = ref(false);
const error = ref(false);
const infoOpen = ref(false);
const eligibleDocumentsAvailable = ref<boolean | undefined>(
  restoredSnapshot?.eligibleDocumentsAvailable,
);
const infoControl = ref<HTMLElement>();
const searchInputElement = ref<HTMLInputElement>();

let searchTimer: number | undefined;
let requestSequence = 0;

const searchTerm = computed(() => readSelectorQueryValue(route.query.q).trim());
const selectedWorkspaceId = computed(() => {
  const workspaceId = readSelectorQueryValue(route.query.workspace).trim();
  return workspaceId || undefined;
});
const mode = computed(() =>
  getPresentationAnalyticsSelectorMode({
    searchTerm: searchTerm.value,
    workspaceId: selectedWorkspaceId.value,
  }),
);
const breadcrumbItems = computed(() => [
  {
    text: t("presentationAnalyticsSelector.breadcrumb.analytics"),
    href: "/u/analytics",
  },
  {
    text: t("presentationAnalyticsSelector.title"),
  },
]);

const listTitleKey = computed(() => {
  if (mode.value === "search") {
    return "presentationAnalyticsSelector.list.searchResults";
  }
  if (mode.value === "workspace") {
    return "presentationAnalyticsSelector.list.workspaceDocuments";
  }
  return "presentationAnalyticsSelector.list.mostViewed";
});

const emptyStateKey = computed(() => {
  if (mode.value === "search") {
    return "presentationAnalyticsSelector.empty.search";
  }
  if (mode.value === "workspace") {
    return "presentationAnalyticsSelector.empty.workspace";
  }
  if (eligibleDocumentsAvailable.value === false) {
    return "presentationAnalyticsSelector.empty.unavailable";
  }
  return "presentationAnalyticsSelector.empty.activity";
});

async function fetchResults(append = false) {
  const requestId = ++requestSequence;
  const offset = append ? results.value.length : 0;
  const query = selectorQueryForMode({
    mode: mode.value,
    searchTerm: searchTerm.value,
    workspaceId: selectedWorkspaceId.value,
    offset,
  });

  if (append) {
    loadingMore.value = true;
  } else {
    loading.value = true;
    results.value = [];
    hasMore.value = false;
    if (mode.value === "top") {
      eligibleDocumentsAvailable.value = undefined;
    }
  }
  error.value = false;

  try {
    const response = await clientHandle.client
      .query<
        Record<
          string,
          { nodes?: Array<PresentationAnalyticsSelectorResult | null> }
        >
      >(query.document, query.variables, { requestPolicy: "network-only" })
      .toPromise();

    if (requestId !== requestSequence) {
      return;
    }

    if (response.error) {
      throw response.error;
    }

    const responseData = response.data as
      | (Record<
          string,
          {
            nodes?: Array<PresentationAnalyticsSelectorResult | null>;
          }
        > & {
          presentationAnalyticsHasEligibleDocuments?: boolean | null;
        })
      | undefined;

    const page = (responseData?.[query.responseKey]?.nodes ?? []).filter(
      (result): result is PresentationAnalyticsSelectorResult =>
        result != null && result.document != null,
    );

    if (mode.value === "top") {
      eligibleDocumentsAvailable.value =
        responseData?.presentationAnalyticsHasEligibleDocuments ?? false;
    }

    results.value = append ? [...results.value, ...page] : page;
    hasMore.value =
      mode.value !== "top" && page.some((result) => result.hasMore);

    await nextTick();
  } catch {
    if (requestId === requestSequence) {
      error.value = true;
      hasMore.value = false;
    }
  } finally {
    if (requestId === requestSequence) {
      loading.value = false;
      loadingMore.value = false;
    }
  }
}

function updateQuery({
  q = searchTerm.value,
  workspace = selectedWorkspaceId.value,
}: {
  q?: string;
  workspace?: string | null;
}) {
  void router.replace({
    name: "PresentationAnalyticsSelector",
    query: {
      ...route.query,
      q: q?.trim() || undefined,
      workspace: workspace || undefined,
    },
    state: presentationAnalyticsSelectorEntryState(selectorEntryId),
  });
}

function scheduleSearch() {
  window.clearTimeout(searchTimer);
  searchTimer = window.setTimeout(() => {
    updateQuery({ q: searchInput.value });
  }, 300);
}

function submitSearch() {
  window.clearTimeout(searchTimer);
  updateQuery({ q: searchInput.value });
}

function clearSearch() {
  searchInput.value = "";
  window.clearTimeout(searchTimer);
  updateQuery({ q: "" });
  void nextTick(() => searchInputElement.value?.focus());
}

function selectWorkspace(workspaceId?: string) {
  updateQuery({ workspace: workspaceId ?? null });
}

function toggleInfo() {
  infoOpen.value = true;
}

function restoreScrollPosition(scrollTop: number) {
  void nextTick(() => {
    const scrollElement = document.querySelector<HTMLElement>("#content");
    if (scrollElement) {
      scrollElement.scrollTop = scrollTop;
    }
  });
}

onClickOutside(infoControl, () => {
  infoOpen.value = false;
});

watch(
  [searchTerm, selectedWorkspaceId],
  ([querySearch]) => {
    searchInput.value = querySearch;

    if (restoredSnapshot) {
      const scrollTop = restoredSnapshot.scrollTop;
      restoredSnapshot = null;
      restoreScrollPosition(scrollTop);
      return;
    }

    void fetchResults();
  },
  { immediate: true },
);

onBeforeRouteLeave((to: RouteLocationNormalized) => {
  if (to.name !== "PresentationAnalyticsDetail") {
    return true;
  }

  savePresentationAnalyticsSelectorSnapshot(selectorEntryId, route.fullPath, {
    results: [...results.value],
    hasMore: hasMore.value,
    eligibleDocumentsAvailable: eligibleDocumentsAvailable.value,
    scrollTop: document.querySelector<HTMLElement>("#content")?.scrollTop ?? 0,
  });

  return true;
});

onBeforeUnmount(() => {
  window.clearTimeout(searchTimer);
  requestSequence += 1;
});
</script>

<template>
  <main class="pane page_content presentation_selector">
    <Breadcrumbs :items="breadcrumbItems" />

    <header class="page_header">
      <div>
        <div class="title_line">
          <h1>{{ $t("presentationAnalyticsSelector.title") }}</h1>
          <div
            ref="infoControl"
            class="info_control"
            :class="{ open: infoOpen }"
            @mouseenter="infoOpen = true"
            @mouseleave="infoOpen = false"
            @focusin="infoOpen = true"
            @focusout="infoOpen = false"
            @keydown.esc.stop.prevent="infoOpen = false"
          >
            <button
              type="button"
              class="info_button"
              :aria-label="$t('presentationAnalyticsSelector.info.label')"
              aria-describedby="presentation-analytics-video-info"
              :aria-expanded="infoOpen"
              @click="toggleInfo"
            >
              <span class="material-symbols-outlined">info</span>
            </button>
            <div
              id="presentation-analytics-video-info"
              class="info_popover"
              role="tooltip"
            >
              {{ $t("presentationAnalyticsSelector.info.video") }}
            </div>
          </div>
        </div>
        <p>{{ $t("presentationAnalyticsSelector.description") }}</p>
      </div>
    </header>

    <section
      class="selector_controls"
      :aria-label="$t('presentationAnalyticsSelector.filtersLabel')"
    >
      <form class="search_control" role="search" @submit.prevent="submitSearch">
        <span class="material-symbols-outlined" aria-hidden="true">search</span>
        <input
          ref="searchInputElement"
          v-model="searchInput"
          type="search"
          :placeholder="$t('presentationAnalyticsSelector.search.placeholder')"
          :aria-label="$t('presentationAnalyticsSelector.search.label')"
          data-test="presentation-analytics-search"
          @input="scheduleSearch"
        />
        <button
          v-if="searchInput"
          type="button"
          class="clear_search"
          :aria-label="$t('presentationAnalyticsSelector.search.clear')"
          @click="clearSearch"
        >
          <span class="material-symbols-outlined">close</span>
        </button>
      </form>

      <PresentationAnalyticsWorkspaceFilter
        :model-value="selectedWorkspaceId"
        @change="selectWorkspace"
      />
    </section>

    <section class="results_section" :aria-busy="loading || loadingMore">
      <div class="list_heading">
        <h2>{{ $t(listTitleKey) }}</h2>
        <span v-if="mode === 'top'" class="period_context">
          {{ $t("presentationAnalyticsSelector.list.last90Days") }}
        </span>
      </div>

      <div
        class="results_status"
        aria-live="polite"
        data-test="presentation-analytics-results-status"
      >
        <span v-if="!loading && !error">
          {{
            $t("presentationAnalyticsSelector.list.resultCount", {
              count: results.length,
            })
          }}
        </span>
      </div>

      <div v-if="loading" class="skeleton_list" data-test="selector-loading">
        <div v-for="index in 6" :key="index" class="skeleton_row">
          <span class="skeleton_rank"></span>
          <span class="skeleton_thumbnail"></span>
          <span class="skeleton_text"></span>
          <span class="skeleton_stat"></span>
        </div>
      </div>

      <div v-else-if="error" class="state_panel" role="alert">
        <span class="material-symbols-outlined" aria-hidden="true">
          cloud_off
        </span>
        <p>{{ $t("presentationAnalyticsSelector.error") }}</p>
        <button type="button" class="primary" @click="fetchResults()">
          {{ $t("presentationAnalyticsSelector.retry") }}
        </button>
      </div>

      <div v-else-if="results.length === 0" class="state_panel">
        <span class="material-symbols-outlined" aria-hidden="true">
          analytics
        </span>
        <p>{{ $t(emptyStateKey) }}</p>
      </div>

      <div v-else class="result_list">
        <PresentationAnalyticsDocumentRow
          v-for="result in results"
          :key="result.documentId"
          :result="result"
          :selector-entry-id="selectorEntryId"
        />
      </div>

      <div v-if="hasMore && !loading && !error" class="load_more">
        <button
          type="button"
          :disabled="loadingMore"
          :aria-busy="loadingMore"
          data-test="presentation-analytics-load-more"
          @click="fetchResults(true)"
        >
          {{
            loadingMore
              ? $t("presentationAnalyticsSelector.list.loadingMore")
              : $t("presentationAnalyticsSelector.list.loadMore")
          }}
        </button>
      </div>
    </section>
  </main>
</template>

<style scoped>
.presentation_selector {
  box-sizing: border-box;
  max-width: 72rem;
  min-height: 100%;
  padding-bottom: 4rem;
}

.page_header {
  display: flex;
  justify-content: space-between;
  margin: 1.5rem 0;
}

.title_line {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.title_line h1 {
  margin: 0;
  font-size: 1.65rem;
  font-weight: 500;
}

.page_header p {
  max-width: 44rem;
  margin: 0.5rem 0 0;
  color: var(--classic-text-muted, #736c64);
  line-height: 1.5;
}

.info_control {
  position: relative;
}

.info_button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: var(--classic-text-muted, #736c64);
  cursor: pointer;
}

.info_button:hover,
.info_button:focus-visible {
  background: var(--classic-surface-hover, #f5f5f5);
  color: inherit;
}

.info_popover {
  display: none;
  position: absolute;
  left: 0;
  top: calc(100% + 0.35rem);
  z-index: 45;
  width: min(22rem, 75vw);
  padding: 0.7rem 0.8rem;
  border-radius: 0.35rem;
  background: var(--classic-tooltip-surface, #404040);
  color: #fff;
  font-size: 0.85rem;
  line-height: 1.45;
  box-shadow: 0 0.5rem 1.5rem rgba(0, 0, 0, 0.2);
}

.info_control.open .info_popover {
  display: block;
}

.selector_controls {
  display: grid;
  grid-template-columns: minmax(18rem, 1fr) auto;
  gap: 0.75rem;
  align-items: start;
  margin-bottom: 1.8rem;
}

.search_control {
  display: flex;
  align-items: center;
  height: 2.4rem;
  border: 1px solid var(--classic-border-strong, #bbb);
  border-radius: 0.3rem;
  background: var(--classic-surface, #fff);
}

.search_control:focus-within {
  border-color: var(--classic-text-strong, #1a1918);
  box-shadow: 0 0 0 2px rgba(52, 181, 186, 0.18);
}

.search_control > .material-symbols-outlined {
  padding-left: 0.75rem;
  color: var(--classic-text-muted, #736c64);
  font-size: 1.2rem;
}

.search_control input {
  flex: 1;
  min-width: 0;
  height: 100%;
  padding: 0 0.6rem;
  border: 0;
  outline: 0;
  background: transparent;
  color: inherit;
}

.clear_search {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.4rem;
  border: 0;
  background: transparent;
  color: inherit;
  cursor: pointer;
}

.list_heading {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 1rem;
  padding-bottom: 0.7rem;
  border-bottom: 1px solid var(--classic-border, #ddd);
}

.list_heading h2 {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 500;
}

.period_context,
.results_status {
  color: var(--classic-text-muted, #736c64);
  font-size: 0.82rem;
}

.results_status {
  min-height: 1.2rem;
  padding-top: 0.4rem;
  text-align: right;
}

.skeleton_row {
  display: grid;
  grid-template-columns: 2.25rem 4.5rem minmax(0, 1fr) 8rem;
  gap: 0.8rem;
  align-items: center;
  min-height: 5rem;
  padding: 0.65rem 0.8rem;
  border-bottom: 1px solid var(--classic-border-subtle, #ececec);
}

.skeleton_row > span {
  display: block;
  border-radius: 0.3rem;
  background: linear-gradient(
    90deg,
    var(--classic-surface-raised, #f1f1f1) 25%,
    var(--classic-surface-inset, #f8f8f8) 50%,
    var(--classic-surface-raised, #f1f1f1) 75%
  );
  background-size: 200% 100%;
  animation: skeleton-pulse 1.4s infinite linear;
}

.skeleton_rank {
  width: 1.2rem;
  height: 1rem;
}

.skeleton_thumbnail {
  width: 4.5rem;
  height: 3.75rem;
}

.skeleton_text {
  width: min(22rem, 70%);
  height: 1.1rem;
}

.skeleton_stat {
  width: 8rem;
  height: 1rem;
}

.state_panel {
  display: flex;
  min-height: 16rem;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.7rem;
  color: var(--classic-text-muted, #736c64);
  text-align: center;
}

.state_panel > .material-symbols-outlined {
  font-size: 3.5rem;
  opacity: 0.45;
}

.state_panel p {
  max-width: 34rem;
  margin: 0;
}

.state_panel button,
.load_more button {
  min-height: 2.4rem;
  padding: 0.4rem 1rem;
  border: 1px solid var(--classic-border-strong, #bbb);
  border-radius: 0.3rem;
  background: var(--classic-surface, #fff);
  color: inherit;
  cursor: pointer;
}

.state_panel button.primary {
  border-color: var(--classic-accent, #34b5ba);
}

.load_more {
  display: flex;
  justify-content: center;
  padding-top: 1.5rem;
}

.load_more button:disabled {
  cursor: wait;
  opacity: 0.65;
}

@keyframes skeleton-pulse {
  from {
    background-position: 200% 0;
  }
  to {
    background-position: -200% 0;
  }
}

@media (max-width: 680px) {
  .presentation_selector {
    padding-right: 1rem;
    padding-left: 1rem;
  }

  .selector_controls {
    grid-template-columns: 1fr;
  }

  .list_heading {
    align-items: flex-start;
    flex-direction: column;
    gap: 0.25rem;
  }

  .results_status {
    text-align: left;
  }

  .skeleton_row {
    grid-template-columns: 1.5rem 3.5rem minmax(0, 1fr);
    padding-right: 0.25rem;
    padding-left: 0.25rem;
  }

  .skeleton_thumbnail {
    width: 3.5rem;
    height: 3.2rem;
  }

  .skeleton_stat {
    display: none !important;
  }
}
</style>
